import { HttpErrorResponse } from '@angular/common/http';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  EmbeddedViewRef,
  Injectable,
  Injector
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BrowserDetectService, NewTabService } from '@rct/lynx-lobbies-shared-lib';
import {
  ApiService,
  Category,
  Game,
  LaunchGameUrl,
  Locale,
  Platform,
} from '@rct/lynx-lobbies-shared-lib';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap
} from 'rxjs/operators';
import { GameFrameComponent } from '../../pages/home/category/game-frame/game-frame.component';

@Injectable({
  providedIn: 'root',
})
export class LynxGameService {
  private newTabService = new NewTabService(new BrowserDetectService());
  private gameByCategoryId = new BehaviorSubject<{ [key: string]: Game[] }>(
    null
  );
  private allGames = new BehaviorSubject<Game[]>([]);

  constructor(
    private translateService: TranslateService,
    private apiService: ApiService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  setGameData(categories: Category[]): void {
    const gameByCategoryId = {};
    const allGames = [];
    this.setSubCategoriesGames(categories, allGames, gameByCategoryId);
    this.gameByCategoryId.next(gameByCategoryId);
    this.allGames.next(allGames);
  }

  openGame(gameCode: string, isMobile: boolean, categoryId: string): Observable<Window> {
    const platform: Platform = isMobile ? Platform.Mobile : Platform.Desktop;
    const locale: Locale = this.translateService.currentLang as Locale;
    const params = categoryId ? { categoryId: categoryId } : {};
    return this.apiService
      .getLaunchGameUrl$(gameCode, platform, locale)
      .pipe(
        map((response: LaunchGameUrl) => response.launchUrl),
        mergeMap((url) => {
          if (isMobile) {
            this.openGameInIFrame(url);
            return of(null);
          }
          return this.newTabService.open(url);
        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(err);
        })
      );
  }

  getGames(categoryId: string): Observable<Game[]> {
    return this.gameByCategoryId.asObservable().pipe(
      filter(
        (gameByCategoryId: { [key: string]: Game[] }) => !!gameByCategoryId
      ),
      map(
        (gameByCategoryId: { [key: string]: Game[] }) =>
          gameByCategoryId[categoryId]
      ),
      debounceTime(300)
    );
  }

  getSearchGames(): Observable<Game[]> {
    return this.allGames.asObservable();
  }

  openGameInIFrame(url: string): void {
    const componentRef = this.componentFactoryResolver
      .resolveComponentFactory(GameFrameComponent)
      .create(this.injector);

    componentRef.instance.url = url;
    componentRef.instance.back.subscribe(() => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    });

    this.appRef.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
  }

  private setSubCategoriesGames(
    categories: Category[],
    allGames: Game[],
    gameByCategoryId
  ): void {
    categories.forEach((category: Category) => {
      gameByCategoryId[category.categoryId] = category.games;
      allGames.push(...category.games);
      if (category.subCategories) {
        this.setSubCategoriesGames(
          category.subCategories,
          allGames,
          gameByCategoryId
        );
      }
    });
  }
}
